import { cn } from '@/lib/utils';
import Markdown from '@/components/markdown/Markdown';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import type { RespondentMap } from './useRespondentMap';

/**
 * Match `[R12]` / `[Q<uuid>]` / `[T3]` citation tokens. Same pattern
 * the chat bubble uses so both surfaces tokenise identically. The
 * `g` flag is required for `.split(...)`; `i` is for `[Q...]` UUIDs.
 */
const CITATION_PATTERN = /(\[R\d+\])|(\[Q[a-z0-9-]+\])|(\[T\d+\])/gi;

interface CitationPillTextProps {
  /**
   * The text to render. Will be tokenised on `[Rn]` / `[Qx]` / `[Tn]`
   * and rendered as a mix of inline markdown + clickable pills.
   */
  text: string;
  /**
   * Lookup for `[Rn]` pills — when present and the respondent has a
   * conversation_id, the pill becomes clickable and opens the
   * transcript panel. Missing entries render as a disabled pill.
   */
  respondentMap: RespondentMap;
  /**
   * Layout hint:
   *   - `'paragraph'` (default) wraps text chunks in `<p>` blocks
   *     so paragraph breaks in body content render correctly.
   *   - `'inline'` renders text chunks via Markdown's
   *     `isParagraphSpanEl` so the whole thing is inline-safe (used
   *     by chat bubbles where the parent is already a `<span>`).
   */
  variant?: 'paragraph' | 'inline';
  /** Optional className applied to the outer wrapper. */
  className?: string;
}

/**
 * D.3 + theme/markdown artifact pill rendering — shared across the
 * chat bubble (`MessageBubble`) and the two artifact renderers that
 * carry `[Rn]` tokens in their body content (`ArtifactThemeSummary`
 * for theme bodies, `ArtifactMarkdownReport` for free-form report
 * content).
 *
 * Click behaviour by token type:
 *   - `[Rn]` → opens the respondent's transcript panel (when their
 *             conversation_id is known via `respondentMap`); inert
 *             grey pill otherwise.
 *   - `[Q<uuid>]` → hash-anchor `#question-<id>` for a future
 *                   Results-page handler; styled green pill in the
 *                   meantime so the token isn't raw text.
 *   - `[T<n>]` → inert violet pill (no navigation target yet —
 *               theme IDs are positional).
 */
function CitationPillText({
  text,
  respondentMap,
  variant = 'paragraph',
  className,
}: CitationPillTextProps) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  if (!text) return null;

  const parts = text.split(CITATION_PATTERN).filter((p) => p !== undefined);

  // No citations found — render the whole text as a single Markdown
  // block. Cheaper than the per-part loop for the common case where
  // a paragraph contains no citations.
  if (parts.length === 1) {
    return (
      <div className={className}>
        <Markdown isParagraphSpanEl={variant === 'inline'}>{text}</Markdown>
      </div>
    );
  }

  const isInline = variant === 'inline';
  const Wrapper = isInline ? 'span' : 'div';

  return (
    <Wrapper className={cn('leading-relaxed', className)}>
      {parts.map((part, i) => {
        if (!part) return null;
        const rMatch = part.match(/^\[R(\d+)\]$/);
        if (rMatch) {
          const respondentIdx = parseInt(rMatch[1], 10);
          const ref = respondentMap.get(respondentIdx);
          const isClickable = !!ref;
          return (
            <button
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              type="button"
              disabled={!isClickable}
              aria-disabled={!isClickable}
              className={cn(
                'inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full transition-opacity align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
                isClickable
                  ? 'bg-primary opacity-90 hover:opacity-100 cursor-pointer'
                  : 'bg-neutral-300 opacity-60 cursor-not-allowed',
              )}
              aria-label={
                isClickable
                  ? `View transcript for Respondent ${rMatch[1]}`
                  : `Respondent ${rMatch[1]} (transcript not available in this view)`
              }
              title={
                isClickable
                  ? `View Respondent ${rMatch[1]}'s transcript`
                  : `Respondent ${rMatch[1]}`
              }
              onClick={() => {
                if (ref) openTranscriptPanel(ref.conversationId, ref.messageId, ref.questionId);
              }}
            >
              {rMatch[1]}
            </button>
          );
        }
        const qMatch = part.match(/^\[Q([a-z0-9-]+)\]$/i);
        if (qMatch) {
          const questionId = qMatch[1];
          return (
            <a
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              href={`#question-${questionId}`}
              aria-label={`Jump to question ${questionId}`}
              title={`Question ${questionId}`}
              className="inline-flex items-center justify-center h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-1 no-underline"
            >
              Q·{questionId.slice(0, 4)}
            </a>
          );
        }
        const tMatch = part.match(/^\[T(\d+)\]$/);
        if (tMatch) {
          return (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              aria-label={`Theme ${tMatch[1]}`}
              title={`Theme ${tMatch[1]}`}
              className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-violet-600 align-text-top"
            >
              T{tMatch[1]}
            </span>
          );
        }
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Markdown key={i} isParagraphSpanEl={isInline}>
            {part}
          </Markdown>
        );
      })}
    </Wrapper>
  );
}

export default CitationPillText;
